import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { CircleNotch as Loader2 } from '@phosphor-icons/react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'link' | 'gold';
type Size = 'xs' | 'sm' | 'md' | 'lg' | 'icon';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-xs hover:shadow-md',
  secondary:
    'bg-card text-foreground border border-border hover:bg-muted hover:border-border',
  outline:
    'border border-border/80 bg-card text-foreground hover:bg-muted/60 hover:border-border',
  ghost:
    'text-foreground hover:bg-muted/60',
  destructive:
    'bg-danger text-white hover:bg-red-700 active:bg-red-800 shadow-xs',
  link:
    'text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline underline-offset-4',
  gold:
    'bg-card text-foreground border border-border hover:bg-muted',
};

const SIZE: Record<Size, string> = {
  xs: 'h-7 px-2.5 text-xs gap-1 rounded-md',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-6 text-sm gap-2 rounded-lg',
  icon: 'h-9 w-9 p-0 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  ({ className, variant = 'primary', size = 'md', loading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          VARIANT[variant],
          SIZE[size],
          className,
        )}
        {...props}
      >
        {/* `shrink-0` on the icon slots: inside a fixed-width cell (the mobile action grid gives
            every button the same width) a long label squeezed the leading icon down to a sliver
            — "Schedule hearing" rendered its calendar as a dot. The label is what should wrap. */}
        {loading ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        ) : (
          leftIcon && <span className="flex shrink-0 items-center">{leftIcon}</span>
        )}
        {children}
        {!loading && rightIcon && <span className="flex shrink-0 items-center">{rightIcon}</span>}
      </button>
    );
  },
);
Button.displayName = 'Button';
